import { Dispatch, forwardRef, SetStateAction, useMemo } from 'react'
import { Box, Button, Skeleton, Typography } from '@mui/material'
import { ArrowUpIcon } from '@heroicons/react/24/outline'
import dayjs from 'dayjs'
import theme from 'theme'
import { useNavigate } from 'react-router-dom'

/* Utils */
import { ChartData, CPISeverity, DataStatus, DEVICES_COLUMN_NAME, SnowflakeChartData } from 'models'
import {
  formatPerformanceUnit,
  formatRecentValueHtml,
  getRecentValueDate,
  instanceOfSnowflake
} from '../../../../library/utils'
import { dateFormat, getHealthDirection } from 'lib/utils'

/* Components */
import NoPerformanceData from '../components/NoPerformanceData'
import { DescriptionSection, DescriptionWrapper, LabelsMenuWrapper, StatusValueText } from '../trendsTab.styles'
import { DefaultLastDayProps } from '../TrendsTab'
import NotReadyIcon from './components/NotReadyIcon'
import { DevicesTabs } from '../../../useLibraryDetailsData'
import { useLibraryDetails } from 'contexts/LibraryDetailsContext'

type Ref = HTMLDivElement | null

interface Props {
  chartData: ChartData | SnowflakeChartData | null
  selectedSeverity: CPISeverity
  setDrawerOpen: Dispatch<SetStateAction<boolean>>
  zone: string
  status?: DataStatus
  renderSeverities: () => JSX.Element | null
  defaultLastDayProps: DefaultLastDayProps
  labelCount: number
  initialLoad: boolean
}

const TrendsChartDescription = forwardRef<Ref, Props>(
  ({ chartData, selectedSeverity, setDrawerOpen, zone, status, defaultLastDayProps, labelCount, initialLoad }, ref) => {
    const {
      manifestData,
      setMode,
      isMediumHeight,
      emptyInfoTooltipOpen,
      setEmptyInfoTooltipOpen,
      handleEmptyInfoClick,
      setTabValue,
      snowflakeManifest,
      setRedirectToMode,
      predictionData,
      predictionModalOpen,
      defaultRecentValueDate,
      selectedDataSourceIds,
      dataSourceOptions,
      metricManifest
    } = useLibraryDetails()
    const navigate = useNavigate()
    if (!manifestData) return null

    const handleClick = () => {
      setMode('show-0')
      setRedirectToMode('show-0')
      setDrawerOpen(true)
    }

    const getPredictionLastValue = () => {
      if (!predictionData) return ''
      const lastValue = predictionData.values[predictionData.values.length - 1].yValue

      const manifestUnit = manifestData?.ui.trend_chart.user_input.visualization_param.unit
      const unitText = formatPerformanceUnit(manifestUnit)

      return `${lastValue.toFixed(2)}` + `<span>${unitText}</span>`
    }

    const checkUnit = () => {
      const unit = manifestData?.ui.trend_chart.user_input.visualization_param.unit
      return ['assets', 'alerts', 'incidents', 'events'].includes(unit)
    }

    const getMidSectionTitle = () => {
      const title = manifestData.title

      if (manifestData.name === 'cpi015') {
        return 'Number of Vulnerabilities'
      }
      if (manifestData.name === 'cpi004') {
        return 'Number of Detections'
      }
      if (title.includes('Rate')) {
        return 'Number of Emails'
      }
      if (manifestData.name === 'cpi074') {
        return 'Number of Applications'
      }
      if (title.includes('Percent of ')) {
        return title.replace('Percent of ', '')
      }
      if (title.includes('Percent Of ')) {
        return title.replace('Percent Of ', '')
      }
      if (title.includes('Number of ')) {
        return title.replace('Number of ', '')
      }
      if (manifestData.name === 'cpi997') {
        return 'Open/Closed Detections'
      }

      const c = title.split(' ')
      if (c[c.length - 1] === 'Rate') {
        return title
      }
      if (title.includes('Mean Time To')) {
        const d = title.split(' ')
        const res = d.slice(4)

        return `Number of ${res.join(' ')}`
      }

      return title
    }

    const recentValueDate = useMemo(() => getRecentValueDate(chartData), [chartData])

    // Round before deciding arrow/color so a sub-0.5 change (e.g. 0.3) reads as
    // a flat 0% with no arrow rather than a rounded "0%" that still points up.
    const recentFindingChange =
      chartData && chartData.lastMonthChange != null ? Math.round(chartData.lastMonthChange) : null

    // Color reflects improvement, not the raw sign: whether a metric going up
    // is good depends on its health direction (lower- vs higher-is-better),
    // derived from the zone, recent value and SLA. The arrow still shows the
    // actual up/down movement and is decoupled from the color. A flat 0 (and
    // the unknown case) is treated as positive (green).
    const recentValue =
      chartData && 'recentValue' in chartData ? chartData.recentValue : chartData?.recentPoint?.yValue ?? null
    const healthDirection =
      chartData && recentValue != null ? getHealthDirection(zone, recentValue, chartData.sla) : 'desc'
    const isRecentFindingImprovement =
      recentFindingChange == null ||
      recentFindingChange === 0 ||
      (healthDirection === 'asc' && recentFindingChange > 0) ||
      (healthDirection === 'desc' && recentFindingChange < 0)

    // Derive the recent value HTML from the live, severity-scoped chartData so the
    // "Recent Finding" card recalculates on every filter change (ONYXIA-8156).
    const recentValueHtml = useMemo(() => formatRecentValueHtml(chartData, manifestData), [chartData, manifestData])

    /*
     * A v4 response withholds recentPoint when no reading is an eligible finding, even with a chart
     * full of real points, so the No Data treatment keys off the point and not off an empty series.
     */
    const hasRecentFinding = chartData
      ? instanceOfSnowflake(chartData)
        ? !!chartData.recentPoint
        : chartData.values && chartData.values.length > 0
      : false

    return (
      <DescriptionWrapper
        sx={{ gap: isMediumHeight ? `${theme.spacing.half}px` : `${theme.spacing.small}px` }}
        status={status}
        zone={zone}
        className="description-wrapper"
        ref={ref}
      >
        {/* Recent Finding */}
        <DescriptionSection className="description-section" isPredictionMode={predictionModalOpen ? 'true' : 'false'}>
          {chartData?.status !== DataStatus.Ready && (
            <Box className="non-ready-wrapper">
              <Typography className="text">Recent Finding:</Typography>
              {initialLoad && chartData && (
                <Box className="status-wrapper not-ready-status">
                  <Typography className="status">{chartData?.status.toLowerCase()}</Typography>
                  <NotReadyIcon />
                </Box>
              )}
            </Box>
          )}

          {chartData && chartData.status === DataStatus.Ready && (
            <Box className="description-section-inner">
              <Box className="most-recent-performance-wrapper">
                <Typography className="text">Recent Finding:</Typography>

                {!initialLoad ? (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={20}
                    animation="wave"
                    sx={{ mt: 2.5, bgcolor: theme.colors.borderMedium }}
                  />
                ) : (
                  recentValueDate && <Typography className="updated-at">{recentValueDate}</Typography>
                )}
              </Box>

              {!initialLoad ? (
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={40}
                  animation="wave"
                  sx={{ mt: 0, bgcolor: theme.colors.borderMedium }}
                />
              ) : (
                <Box className="recent-value-row">
                  {hasRecentFinding ? (
                    <StatusValueText
                      className={checkUnit() ? 'column status' : 'status'}
                      status={status}
                      zone={zone}
                      dangerouslySetInnerHTML={{ __html: recentValueHtml }}
                    />
                  ) : (
                    <NoPerformanceData
                      setEmptyInfoTooltipOpen={setEmptyInfoTooltipOpen}
                      emptyInfoTooltipOpen={emptyInfoTooltipOpen}
                      handleEmptyInfoClick={handleEmptyInfoClick}
                    />
                  )}

                  {hasRecentFinding && (
                    <Box className="last-month-change">
                      {recentFindingChange == null ? (
                        <span className="change-empty">---</span>
                      ) : (
                        <Box className={`change-indicator ${isRecentFindingImprovement ? 'positive' : 'negative'}`}>
                          {recentFindingChange !== 0 && (
                            <ArrowUpIcon className={`arrow-icon ${recentFindingChange < 0 ? 'down' : ''}`} />
                          )}
                          <span className="change-text">{Math.abs(recentFindingChange)}</span>
                        </Box>
                      )}
                      <span className="since-label">since last month</span>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DescriptionSection>

        {predictionModalOpen && (
          <DescriptionSection className="description-section" isPredictionMode={predictionModalOpen ? 'true' : 'false'}>
            {chartData?.status !== DataStatus.Ready && (
              <Box className="non-ready-wrapper">
                <Typography className="text">Predicted Performance:</Typography>
                <Box className="status-wrapper">
                  <Typography className="status">{chartData?.status.toLowerCase()}</Typography>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                    id="gear"
                  >
                    <path
                      d="M20 6.25V10M29.7227 10.2773L27.0711 12.9289M33.75 20H30M12.9289 27.0711L10.2773 29.7227M10 20H6.25M12.9289 12.9289L10.2773 10.2773M20.0002 33.75V30M27.0712 27.0711L29.7229 29.7227"
                      stroke="white"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M27.0703 27.0703L29.722 29.722"
                      stroke="#D9CED6"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M33.75 20H30"
                      stroke="#B1A6AE"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M29.722 10.2734L27.0703 12.9251"
                      stroke="#7F747C"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
              </Box>
            )}

            {chartData && chartData.status === DataStatus.Ready && (
              <Box className="description-section-inner">
                <Box className="most-recent-performance-wrapper">
                  <Typography className="text">Predicted Performance</Typography>
                  <Typography className="updated-at">
                    <span className="severity">
                      {selectedSeverity} Severities{defaultLastDayProps.predictionDate ? ',' : ''}
                    </span>{' '}
                    {defaultLastDayProps.predictionDate
                      ? dayjs(defaultLastDayProps.predictionDate).format(dateFormat.auditLogDefault)
                      : ''}
                  </Typography>
                </Box>

                {chartData.values && chartData.values.length > 0 ? (
                  <StatusValueText
                    className={checkUnit() ? 'column status' : 'status'}
                    status={status}
                    zone={zone}
                    dangerouslySetInnerHTML={{ __html: getPredictionLastValue() }}
                    prediction="true"
                  />
                ) : (
                  <NoPerformanceData
                    setEmptyInfoTooltipOpen={setEmptyInfoTooltipOpen}
                    emptyInfoTooltipOpen={emptyInfoTooltipOpen}
                    handleEmptyInfoClick={handleEmptyInfoClick}
                  />
                )}
              </Box>
            )}
          </DescriptionSection>
        )}

        {/* Middle Section */}
        <DescriptionSection isPredictionMode={predictionModalOpen ? 'true' : 'false'}>
          <Box className="description-section-inner">
            <Box className="most-recent-performance-wrapper">
              <Box className="most-recent-performance-title">
                <Typography className="text">{getMidSectionTitle()}</Typography>
                {/* {showDevicesTab && <NewSectionChip />} */}
              </Box>

              {!initialLoad ? (
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={20}
                  animation="wave"
                  sx={{ mt: 2.5, bgcolor: theme.colors.borderMedium }}
                />
              ) : (
                chartData?.status === DataStatus.Ready && (
                  <Typography className="updated-at">As of yesterday</Typography>
                )
              )}
            </Box>

            {!initialLoad ? (
              <Skeleton
                variant="rectangular"
                width="100%"
                height={40}
                animation="wave"
                sx={{ mt: 0, bgcolor: theme.colors.borderMedium }}
              />
            ) : (
              chartData?.status === DataStatus.Ready && (
                <Box className="devices-footer">
                  <Typography className="devices-count-text">{defaultRecentValueDate.denominator}</Typography>

                  {snowflakeManifest && (
                    <Button
                      className="view-devices-button"
                      onClick={() => {
                        setTabValue(DevicesTabs.OperationalView)
                        const basePath = location.pathname.replace('/trends', '')

                        // Carry the active Trends scoping filters into the Off-SLA Entities tab,
                        // keyed on that table's actual column schema (metricManifest input_schema).
                        type CarriedFilter =
                          | { filterType: 'set'; values: string[]; label?: string }
                          | { filterType: 'numberRange'; gte?: number; lte?: number; label?: string }
                        const trendsFilters: Record<string, CarriedFilter> = {}
                        const offSlaColumns = metricManifest?.druid_spec?.record_batch_spec?.input_schema || []

                        // The "section" selector is a per-CPI dimension whose manifest sections_name is a
                        // logical grouping ("devices", "severities", "routes", "email_sizes"), not an Off-SLA
                        // column name — there's no manifest field linking the two. Map known dimensions to
                        // their Off-SLA column and only carry the scope when that column exists in this
                        // table's schema; anything unmapped is dropped rather than carried as a bogus filter.
                        const SECTION_DIMENSION_TO_COLUMN: Record<string, string> = {
                          devices: DEVICES_COLUMN_NAME.OS_NAME,
                          severities: 'SEVERITY',
                          routes: 'ROUTE_TYPE',
                          email_sizes: 'EMAIL_SIZE_MB'
                        }
                        const visualizationParam = manifestData.ui.trend_chart.user_input.visualization_param
                        const dimension = visualizationParam.sections_name?.toLowerCase()
                        const mappedColumn = dimension ? SECTION_DIMENSION_TO_COLUMN[dimension] : undefined
                        const sectionColumn =
                          mappedColumn && offSlaColumns.some((col) => col.name === mappedColumn)
                            ? mappedColumn
                            : undefined
                        // selectedSeverity is the section's Title Case display_name at runtime (e.g.
                        // "Windows", "Critical", "INBOUND", "≥ 10 MB").
                        const isAllSection = String(selectedSeverity || '').toLowerCase() === CPISeverity.All
                        if (sectionColumn && selectedSeverity && !isAllSection) {
                          if (dimension === 'email_sizes') {
                            // Sizes are cumulative numeric thresholds (≥ N MB). The section's `value` is its
                            // SLA target, not the MB threshold — the MB lives in the `name` (e.g. "above_5mb").
                            // Parse it and carry it as a gte_ predicate, the only form EMAIL_SIZE_MB's
                            // any_/gte_/lte_ schema can express "all emails ≥ N MB".
                            const picked = visualizationParam.sections.find((s) => s.display_name === selectedSeverity)
                            const mb = picked ? Number(String(picked.name).match(/\d+/)?.[0]) : NaN
                            if (!Number.isNaN(mb)) {
                              trendsFilters[sectionColumn] = {
                                filterType: 'numberRange',
                                gte: mb,
                                label: picked?.display_name
                              }
                            }
                          } else {
                            // Set-membership dimensions. selectedSeverity is the section's display_name, which
                            // carries the human casing matching the stored column values (e.g. "OUTBOUND").
                            // Match case-insensitively on the lowercased value, but carry the display_name as
                            // the label so a query-level chip (shown when the value isn't in the column's
                            // lookup, e.g. no data) reads in the same casing as a grid-seeded chip.
                            trendsFilters[sectionColumn] = {
                              filterType: 'set',
                              values: [String(selectedSeverity).toLowerCase()],
                              label: String(selectedSeverity)
                            }
                          }
                        }

                        const hasDataSourceSelection =
                          selectedDataSourceIds.length > 0 &&
                          selectedDataSourceIds[0] !== '' &&
                          selectedDataSourceIds.length < dataSourceOptions.length

                        const hasInstanceColumn = offSlaColumns.some(
                          (col) => col.name === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE
                        )
                        if (hasDataSourceSelection && hasInstanceColumn) {
                          // The Trends data-source picker is instance-level, so it maps to the
                          // ONYXIA_PRODUCT_INSTANCE (Instance Names) column — the product-level
                          // ONYXIA_PRODUCT_NAME lacks instance-only values. That column's set filter is
                          // keyed on the integration display name (its valueGetter maps the raw
                          // instance_name → display name), so carry the display name; createQueryPayload
                          // maps it back to instance_name for the query. The column is hidden by default,
                          // so the Off-SLA tab force-activates it when this filter is carried.
                          const displayNames = selectedDataSourceIds
                            .map(
                              (id) => dataSourceOptions.find((opt) => opt.id === id || opt.instance_name === id)?.name
                            )
                            .filter((name): name is string => Boolean(name))

                          if (displayNames.length > 0) {
                            trendsFilters.ONYXIA_PRODUCT_INSTANCE = {
                              filterType: 'set',
                              values: displayNames
                            }
                          }
                        }

                        navigate(`${basePath}/off-sla-entities`, {
                          state: Object.keys(trendsFilters).length > 0 ? { trendsFilters } : undefined
                        })
                      }}
                    >
                      View Off-SLA Entities
                    </Button>
                  )}
                </Box>
              )
            )}
          </Box>
        </DescriptionSection>

        <LabelsMenuWrapper
          className="labels-section-container"
          isPredictionMode={predictionModalOpen ? 'true' : 'false'}
        >
          <Typography className="labels-title">Labels</Typography>

          <Box className="labels-footer">
            <Typography className="labels-count-text">{labelCount}</Typography>

            <Box className="labels-dropdown-container">
              <Button className="view-labels-button" onClick={handleClick}>
                View Labels
              </Button>
            </Box>
          </Box>
        </LabelsMenuWrapper>
      </DescriptionWrapper>
    )
  }
)

export default TrendsChartDescription
